import React, { useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import ParkingIcon from 'mdi-react/ParkingIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import * as parkingStyles from '../office-parking.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import OfficeParkingCard from '../cards/office-parking-card.component';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import { useParkingsForOffice } from '../../parkings/hooks/parkings.hook';
import type Parking from '../../parkings/models/parking';

interface Props {
  readonly officeId: string | undefined;
}

const OfficeParkingGroup = ({ officeId }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const { parkings } = useParkingsForOffice({ id: officeId ?? '' });

  const [parkingsToShow, setParkingsToShow] = useState<Parking[]>();

  useEffect(() => {
    if (parkings != null) {
      if (isLoading) {
        setIsLoading(false);
      }

      setParkingsToShow(parkings);
    }
  }, [parkings, isLoading]);

  const areas = parkingsToShow ?? [];
  const totalSpaces = areas.reduce((total, parking) => total + parking.capacity, 0);

  const subtitle = t('parking-thresholds-card-subtitle');
  // Both halves are counted nouns, so each picks its own singular/plural form.
  const areasLabel = `${areas.length} ${areas.length === 1 ? t('parking-area') : t('parking-areas')}`;
  const spacesLabel = `${totalSpaces} ${totalSpaces === 1 ? t('parking-space') : t('parking-spaces')}`;
  const countLabel = t('parking-areas-summary').replace('[x]', areasLabel).replace('[y]', spacesLabel);

  const renderBody = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className={style(parkingStyles.rows)}>
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        </div>
      );
    }

    if (areas.length === 0) {
      return (
        <div className={style(surfaceStyles.emptyState)}>
          <ParkingIcon size={30} />
          <span className={style(surfaceStyles.emptyTitle)}>{t('no-parking-spots-available')}</span>
          <span className={style(surfaceStyles.emptyHint)}>{t('no-parking-spots-hint')}</span>
        </div>
      );
    }

    return (
      <>
        <div className={style(parkingStyles.rows)}>
          {areas.map((parking) => (
            <OfficeParkingCard
              key={parking.id ?? parking.name}
              initialParking={parking}
              onUpdated={(updatedParking): void => {
                setParkingsToShow((current) =>
                  current?.map((existing) => (existing.id === updatedParking.id ? updatedParking : existing)),
                );
              }}
            />
          ))}
        </div>
        <div className={style(parkingStyles.note)}>
          <span className={style(parkingStyles.noteIcon)}>
            <InformationOutlineIcon size={18} />
          </span>
          <span>{t('parking-threshold-rounding-note')}</span>
        </div>
      </>
    );
  };

  return (
    <div className={style(surfaceStyles.card)}>
      <div className={style(surfaceStyles.cardHead(isMobile))}>
        <div>
          <h2 className={style(surfaceStyles.cardTitle)}>{t('parking-spots')}</h2>
          <p className={style(surfaceStyles.cardSub)}>{subtitle}</p>
        </div>
        {!isLoading && areas.length > 0 && <span className={style(surfaceStyles.cardMeta)}>{countLabel}</span>}
      </div>
      {renderBody()}
    </div>
  );
};

export default OfficeParkingGroup;
